import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { createFilterBody } from '@/helpers/createFilterParams';
import { FactFilters } from '@/types';

interface UseFactsDownloadProps {
  matterId: string;
  subsetId: string;
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

const useDownloadFacts = ({ matterId, subsetId }: UseFactsDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadFacts = async ({
    title,
    columns,
    downloadFormat,
    styleFormat,
    style,
    documentOptions,
    filters,
    includeSourceDocuments = false,
    orderByDate,
  }: {
    downloadFormat: DownloadFormat;
    styleFormat?: StyleFormat;
    title: string;
    columns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    filters: FactFilters;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }): Promise<ExportResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        style,
        documentOptions,
        includeSourceDocuments,
        orderByDate,
        ...createFilterBody(filters),
      },
    });

    const endpoint = includeSourceDocuments
      ? `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/download-with-documents`
      : `${APIBaseChronos}/api/matter/${matterId}/fact/${subsetId}/download`;

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start facts export');
    }

    return await response.json();
  };

  return {
    downloadMutation: useMutation(downloadFacts, {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to download facts. Please try again.');
      },
    }),
  };
};

export default useDownloadFacts;
